import React, { useState, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { CATALOG } from '../data/catalog';
import { aiFetch } from '../utils/aiFetch';

const EXAMPLES = [
  '60x40 shop with 4 two-post lifts, 2 overhead doors on the south wall, office in the NW corner',
  '80x60 four-bay repair shop with 4-post alignment lift, tire changer, balancer and parts room',
  'Add 2 scissor lifts on the east side of the shop',
  '40x30 quick lube shop with 2 scissor lifts and a waiting room in the NE corner',
];

// Valid equipment IDs — used to validate Claude's output and reject phantom items
const VALID_EQ_IDS = [
  '2post', '4post', 'scissor', 'inground', 'mobile_column',
  'tc', 'bal', 'aln', 'comp', 'wash', 'fjack', 'odrain',
  'toolbox', 'wbench', 'obstruction'
];

// Sanity limits to prevent users from breaking the canvas with extreme values
const LIMITS = {
  minShopSize: 10,
  maxShopSize: 500,
  maxEquipment: 50,
  maxDoors: 10,
};

async function interpretPrompt(userPrompt, currentLayout, getToken) {
  const hasExisting = currentLayout && currentLayout.placed?.length > 0;

  const systemContext = hasExisting ? `
The user currently has this shop layout:
- Shop size: ${currentLayout.shop.w}x${currentLayout.shop.h} ${currentLayout.shop.unit}
- Equipment: ${currentLayout.placed.filter(p => !p.isRoom).map(p => p.name).join(', ') || 'none'}
- Rooms: ${currentLayout.placed.filter(p => p.isRoom).map(p => p.label).join(', ') || 'none'}
- Doors: ${currentLayout.doors.length} doors
The user wants to modify or add to this layout. Keep existing items unless the user explicitly asks to change them.
` : 'The user wants to create a new shop layout from scratch.';

  // Hardened prompt: refuses off-topic requests via structured JSON response
  const prompt = `You are ShopBuilder's layout planning system — an automotive shop layout planner. Never refer to yourself as Claude or AI. You are ShopBuilder.

${systemContext}

The user says: "${userPrompt}"

STEP 1 — VALIDATE INTENT:
First, decide if this request is actually about designing/modifying an automotive shop layout. Valid topics: shop dimensions, lifts, tire equipment, air compressors, service bays, oil drains, workbenches, offices, parts rooms, doors, walls, floor plan layout.

If the request is OFF-TOPIC (food, weather, jokes, code help, personal questions, anything not shop-related), or if it's so vague you cannot infer a layout (e.g. "do something cool"), respond with EXACTLY this JSON and nothing else:

{
  "valid": false,
  "reason": "<one short friendly sentence explaining what went wrong>"
}

STEP 2 — IF VALID, GENERATE LAYOUT:
Return ONLY a valid JSON object with no explanation, no markdown, no backticks:

{
  "valid": true,
  "shop": { "w": <width 10-500 ft>, "h": <depth 10-500 ft>, "unit": "ft" },
  "doors": [
    { "wall": "N|S|E|W", "pos": 0.0-1.0, "type": "overhead|door", "width": 10 }
  ],
  "office": null,
  "equipment": [
    { "id": "<equipment_id>", "cx": <x in feet from NW>, "cy": <y in feet from NW>, "rot": 0, "scaleX": 1, "scaleY": 1, "label": "" }
  ]
}

Available equipment IDs (use ONLY these — no others exist):
- "2post"     = 2-Post Lift (12x16 ft)
- "4post"     = 4-Post Lift (16x24 ft)
- "scissor"   = Scissor Lift (12x22 ft)
- "inground"  = In-Ground Lift (10x24 ft)
- "mobile_column" = Mobile Column Lift (4x4 ft per column)
- "tc"        = Tire Changer (6.2x3.7 ft incl. assist arm)
- "bal"       = Wheel Balancer (4.3x4.8 ft incl. hood)
- "aln"       = Wheel Aligner (8.75x3 ft)
- "comp"      = Air Compressor (3.2x2.2 ft)
- "wash"      = Parts Washer (2.5x1.75 ft)
- "fjack"     = Floor Jack (1.2x2.7 ft)
- "odrain"    = Oil Drain Station (1.8x3.8 ft)
- "toolbox"   = Tool Cabinet (6x2 ft)
- "wbench"    = Workbench (6x4 ft)
- "obstruction" = Pillar/Beam/Column (2x2 ft scalable)

HARD LIMITS:
- Max 50 equipment items total
- Max 10 doors
- Shop size between 10 and 500 ft on each axis
- Reject requests asking for non-existent equipment (Ferraris, robots, etc.)

Layout rules:
- cx and cy are CENTER positions in feet from the NW (top-left) corner
- Place lifts with adequate spacing — at least 4ft between lift edges
- Keep 10-14ft minimum bay depth clearance in front of overhead doors
- Typical bay width per 2-post lift is 12-14ft
- If user mentions an office, reception, parts room etc — put it in the "office" field
- For doors: pos 0.0 = left/top of wall, 1.0 = right/bottom
- Overhead doors are typically 10-14ft wide, walk doors 3-4ft

Return ONLY the JSON, nothing else.`;

  // aiFetch attaches Clerk auth token and handles 401/403 with friendly errors
  const data = await aiFetch({
    model: 'claude-sonnet-5',
    max_tokens: 2000,
    messages: [{ role: 'user', content: prompt }],
  }, getToken);

  const text = data.content?.[0]?.text || '';
  const clean = text.replace(/```json|```/g, '').trim();
  const jsonStart = clean.indexOf('{');
  const jsonEnd = clean.lastIndexOf('}');
  if (jsonStart === -1 || jsonEnd === -1) {
    throw new Error("ShopBuilder makes shops, not sandwiches 😄 Try describing an automotive shop layout instead.");
  }

  const parsed = JSON.parse(clean.slice(jsonStart, jsonEnd + 1));

  // Layer 2: validate the response even if it parsed as JSON
  if (parsed.valid === false) {
    throw new Error(parsed.reason || "ShopBuilder makes shops, not sandwiches 😄 Try describing an automotive shop layout instead.");
  }

  // Sanity-check shop dimensions
  if (!parsed.shop || typeof parsed.shop.w !== 'number' || typeof parsed.shop.h !== 'number') {
    throw new Error("ShopBuilder couldn't figure out the shop dimensions. Try being more specific (e.g. '60x40 ft shop').");
  }
  if (parsed.shop.w < LIMITS.minShopSize || parsed.shop.w > LIMITS.maxShopSize ||
      parsed.shop.h < LIMITS.minShopSize || parsed.shop.h > LIMITS.maxShopSize) {
    throw new Error(`Shop dimensions must be between ${LIMITS.minShopSize} and ${LIMITS.maxShopSize} ft. Try a more reasonable size.`);
  }

  // Strip out any equipment with unknown IDs — keeps the layout safe
  const validEquipment = (parsed.equipment || []).filter(e => VALID_EQ_IDS.includes(e.id));
  const droppedCount = (parsed.equipment || []).length - validEquipment.length;

  // Cap equipment count — protects against runaway "add 1000 lifts" requests
  if (validEquipment.length > LIMITS.maxEquipment) {
    validEquipment.length = LIMITS.maxEquipment;
  }
  parsed.equipment = validEquipment;

  // Cap door count similarly
  if (parsed.doors && parsed.doors.length > LIMITS.maxDoors) {
    parsed.doors.length = LIMITS.maxDoors;
  }

  // Surface a soft warning if items were dropped (logged for debugging)
  if (droppedCount > 0) {
    console.warn(`Dropped ${droppedCount} unknown equipment items from layout`);
  }

  return parsed;
}

export default function PromptImport({ onImport, onClose, currentLayout }) {
  const { getToken } = useAuth(); // Clerk session token for AI API calls
  const [stage, setStage] = useState('input'); // input | thinking | preview | error
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleSubmit = async () => {
    if (!prompt.trim()) return;
    setStage('thinking');
    try {
      const layout = await interpretPrompt(prompt.trim(), currentLayout, getToken);
      setResult(layout);
      setStage('preview');
    } catch (err) {
      console.error('Prompt import failed:', err);
      setError(err.message || 'Could not interpret your description. Try being more specific.');
      setStage('error');
    }
  };

  const handleApply = () => {
    if (!result) return;
    const placed = (result.equipment || []).map(eq => {
      const catalogItem = CATALOG.find(c => c.id === eq.id);
      if (!catalogItem) return null;
      return {
        ...JSON.parse(JSON.stringify(catalogItem)),
        uid: `${Date.now()}-${Math.random()}`,
        cx: Math.max(1, Number(eq.cx) || 10),
        cy: Math.max(1, Number(eq.cy) || 10),
        rot: Number(eq.rot) || 0,
        scaleX: Number(eq.scaleX) || 1,
        scaleY: Number(eq.scaleY) || 1,
        label: eq.label || '',
        _dragging: false,
      };
    }).filter(Boolean);

    const doors = (result.doors || []).map((d, i) => ({
      id: Date.now() + i,
      wall: ['N', 'S', 'E', 'W'].includes(d.wall) ? d.wall : 'S',
      pos: Math.max(0.05, Math.min(0.95, Number(d.pos) || 0.5)),
      type: d.type === 'overhead' ? 'overhead' : 'door',
      width: Number(d.width) || (d.type === 'overhead' ? 10 : 4),
    }));

    onImport({
      shop: { w: Number(result.shop?.w) || 60, h: Number(result.shop?.h) || 40, unit: 'ft' },
      doors,
      office: result.office || null,
      placed,
    });
    onClose();
  };

  const equipment = placed => placed.filter(p => !p.isRoom);
  const rooms = placed => placed.filter(p => p.isRoom);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 24,
    }}>
      <div style={{
        background: '#0a1628', border: '1px solid #162840', borderRadius: 14,
        width: '100%', maxWidth: 580, maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 8px 48px rgba(0,0,0,.7)',
      }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #162840', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#e8f4ff', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#aa66ff' }}>✦</span> AI Layout Builder
            </div>
            <div style={{ fontSize: 11, color: '#4a7a9b', marginTop: 3, lineHeight: 1.5 }}>
              {currentLayout?.placed?.length > 0
                ? 'Describe what to add or change — Shopbuilder will update your layout'
                : 'Describe your shop and Shopbuilder will build the layout for you'}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#4a7a9b', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px' }}>

          {/* INPUT */}
          {stage === 'input' && (
            <div>
              <textarea
                ref={inputRef}
                autoFocus
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSubmit(); }}
                placeholder="Describe your shop layout in plain English..."
                rows={4}
                style={{
                  width: '100%', background: '#07111e', border: '1px solid #1e3a54',
                  borderRadius: 8, color: '#e8f4ff', padding: '12px 14px',
                  fontSize: 13, fontFamily: 'inherit', outline: 'none',
                  resize: 'none', boxSizing: 'border-box', marginBottom: 12,
                  lineHeight: 1.6,
                }}
              />

              {/* Examples */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#4a7a9b', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Examples — click to use
                </div>
                {EXAMPLES.map((ex, i) => (
                  <div key={i} onClick={() => setPrompt(ex)}
                    style={{
                      fontSize: 10, color: '#4a7a9b', padding: '6px 10px',
                      borderRadius: 5, cursor: 'pointer', marginBottom: 4,
                      background: 'rgba(0,80,180,.06)', border: '1px solid #162840',
                      lineHeight: 1.5, transition: 'all .12s',
                    }}
                    onMouseEnter={e => e.target.style.borderColor = '#3399ff44'}
                    onMouseLeave={e => e.target.style.borderColor = '#162840'}
                  >
                    {ex}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={btnS}>Cancel</button>
                <button onClick={handleSubmit} disabled={!prompt.trim()} style={{
                  flex: 1, background: prompt.trim()
                    ? 'linear-gradient(135deg,#5500aa,#8833ff)'
                    : 'rgba(80,80,80,.2)',
                  border: 'none', borderRadius: 8, color: prompt.trim() ? '#fff' : '#666',
                  padding: 11, fontSize: 13, fontWeight: 600,
                  cursor: prompt.trim() ? 'pointer' : 'not-allowed',
                  fontFamily: 'inherit',
                }}>
                  ✦ Generate Layout
                </button>
              </div>
              <div style={{ marginTop: 8, fontSize: 9, color: '#2a4a6a', textAlign: 'center' }}>
                Ctrl+Enter to submit
              </div>
            </div>
          )}

          {/* THINKING */}
          {stage === 'thinking' && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 13, color: '#aa66ff', fontWeight: 600, marginBottom: 8 }}>
                Building your layout...
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', marginBottom: 8, fontStyle: 'italic' }}>
                "{prompt}"
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', lineHeight: 1.6, marginBottom: 24 }}>
                Shopbuilder is planning equipment placement,<br />
                door positions, and room layout.
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6 }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#aa66ff',
                    animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                  }} />
                ))}
              </div>
              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 0.3; transform: scale(0.8); }
                  50% { opacity: 1; transform: scale(1.2); }
                }
              `}</style>
            </div>
          )}

          {/* PREVIEW */}
          {stage === 'preview' && result && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#00cc88', marginBottom: 10, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                ✓ Layout Generated
              </div>

              {/* Prompt summary */}
              <div style={{ padding: '8px 12px', background: 'rgba(170,102,255,.08)', border: '1px solid #aa66ff33', borderRadius: 6, marginBottom: 12, fontSize: 11, color: '#aa66ff', fontStyle: 'italic' }}>
                "{prompt}"
              </div>

              {/* Summary rows */}
              {[
                { label: 'Shop Size', value: `${result.shop?.w || '?'} × ${result.shop?.h || '?'} ft` },
                { label: 'Office/Rooms', value: result.office ? `${result.office.corner} corner (${result.office.w}×${result.office.h} ft)` : 'None' },
                { label: 'Doors', value: `${result.doors?.length || 0} (${result.doors?.filter(d => d.type === 'overhead').length || 0} overhead, ${result.doors?.filter(d => d.type === 'door').length || 0} walk)` },
                { label: 'Equipment', value: `${result.equipment?.length || 0} items` },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #0d1e30', fontSize: 11 }}>
                  <span style={{ color: '#4a7a9b', fontWeight: 600 }}>{label}</span>
                  <span style={{ color: '#c8dff0' }}>{value}</span>
                </div>
              ))}

              {/* Equipment list */}
              {(result.equipment || []).length > 0 && (
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: '#4a7a9b', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                    Equipment Breakdown
                  </div>
                  {(result.equipment || []).map((eq, i) => {
                    const cat = CATALOG.find(c => c.id === eq.id);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #0d1e30', fontSize: 11 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: cat?.border || '#888', flexShrink: 0 }} />
                        <span style={{ color: '#c8dff0', flex: 1 }}>{cat?.name || eq.id}</span>
                        {eq.label && <span style={{ color: '#ff9900', fontSize: 10 }}>{eq.label}</span>}
                        <span style={{ color: '#4a7a9b', fontFamily: 'monospace', fontSize: 10 }}>
                          @ {(Number(eq.cx) || 0).toFixed(0)}, {(Number(eq.cy) || 0).toFixed(0)} ft
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ padding: '10px 12px', background: 'rgba(255,170,0,.07)', border: '1px solid #ff990033', borderRadius: 6, fontSize: 11, color: '#ffaa44', marginBottom: 16, lineHeight: 1.6 }}>
                <strong>Review before applying.</strong> All positions can be adjusted in the editor after import.
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setStage('input'); setResult(null); }} style={btnS}>
                  ← Try Again
                </button>
                <button onClick={handleApply} style={{
                  flex: 1, background: 'linear-gradient(135deg,#5500aa,#8833ff)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  padding: 11, fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                  Apply Layout →
                </button>
              </div>
            </div>
          )}

          {/* ERROR */}
          {stage === 'error' && (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#ff6644', marginBottom: 8 }}>
                Could not generate layout
              </div>
              <div style={{ fontSize: 11, color: '#4a7a9b', lineHeight: 1.7, marginBottom: 20 }}>{error}</div>
              <div style={{ padding: '12px 14px', background: '#07111e', border: '1px solid #162840', borderRadius: 6, marginBottom: 20, textAlign: 'left', lineHeight: 1.8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#c8dff0', marginBottom: 6 }}>Tips for better results:</div>
                {[
                  'Include shop dimensions (e.g. "60x40 ft shop")',
                  'Specify number of bays or lifts (e.g. "4 two-post lifts")',
                  'Mention door locations (e.g. "2 overhead doors on the south wall")',
                  'Name any rooms needed (e.g. "office in the NW corner")',
                ].map((tip, i) => (
                  <div key={i} style={{ fontSize: 10, color: '#4a7a9b' }}>• {tip}</div>
                ))}
              </div>
              <button onClick={() => { setStage('input'); setError(''); }} style={{ ...btnS, width: '100%', padding: 11 }}>
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const btnS = {
  background: 'transparent', border: '1px solid #1e3a54',
  borderRadius: 8, color: '#6a9fc0', fontSize: 12,
  fontWeight: 500, padding: '10px 18px',
  cursor: 'pointer', fontFamily: 'inherit',
};